import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignup } from '../../hooks/useSignup';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ClipLoader } from 'react-spinners';
import { showToast } from '../../utils/Toast';
import bidcaribbean from '../../assets/lux-logo/logo-tag.png';
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { trackAnalyticsEvent } from '../../utils/analytics';

const SignupPopup = ({ isOpen, onClose, onSignupSuccess, onSignupStart, openType = "" }) => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const navigate = useNavigate();
  const { signup, isLoading } = useSignup();

  // A/B testing headlines
  const headlines = [
    "Free Registration — No license required to bid",
    "Start bidding today — Free account, takes 10 seconds",
    "Unlock live auctions & the duty calculator — Free"
  ];

  // Randomly select a headline
  const [selectedHeadline] = useState(() => {
    const storedHeadline = localStorage.getItem('signup_popup_headline');
    if (storedHeadline) {
      return storedHeadline;
    }
    const randomHeadline = headlines[Math.floor(Math.random() * headlines.length)];
    localStorage.setItem('signup_popup_headline', randomHeadline);
    return randomHeadline;
  });

  // Track popup shown
  React.useEffect(() => {
    if (isOpen) {
      trackAnalyticsEvent('popup_shown');
    }
  }, [isOpen]);

  // Validation schema for step 2
  const signupValidationSchema = Yup.object().shape({
    username: Yup.string().required("Full name is required"),
    phone: Yup.string()
      .required("Phone number is required")
      .min(10, "Please enter a valid phone number")
      .test("phone", "Please enter a valid phone number", (value) => {
        if (!value) return false;
        // Remove the country code prefix (e.g., +1) and any non-digit characters
        const phoneNumberWithoutCountryCode = value.replace(/^\+\d+/, '').replace(/\D/g, '');
        return phoneNumberWithoutCountryCode.length >= 10;
      }),
    password: Yup.string().required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Confirm Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      username: "",
      phone: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: signupValidationSchema,
    onSubmit: async (values) => {
      const signupData = {
        username: values.username,
        email: email,
        phone: values.phone,
        password: values.password,
        confirmPassword: values.confirmPassword,
      };

      const { success, message } = await signup(signupData);
      if (success) {
        showToast(message, 'success');
        localStorage.setItem('signup_popup_completed', 'true');
        localStorage.setItem('signed_in', 'true');
        trackAnalyticsEvent('signup_completed');
        if (openType === 'calculator_unlock') {
          // mark calculator unlocked for analytics once
          trackAnalyticsEvent('calculator_unlocked');
        }
        // Show success modal
        document.getElementById("signup_popup_success_modal").showModal();
        onSignupSuccess();
      } else {
        showToast(message, 'error');
      }
    },
    validateOnChange: true,
    validateOnBlur: true,
  });

  const handleEmailContinue = () => {
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStep(2);
      onSignupStart(); // Track that user started signup process
      trackAnalyticsEvent('signup_started');
    } else {
      showToast('Please enter a valid email address', 'error');
    }
  };

  const handleClose = () => {
    localStorage.setItem('signup_popup_closed', Date.now().toString());
    trackAnalyticsEvent('popup_dismissed');
    onClose();
  };

  const handleNotNow = () => {
    // localStorage.setItem('signup_popup_dismissed', Date.now().toString());
    trackAnalyticsEvent('popup_dismissed');
    onClose();
  };

  const closeSuccessModal = () => {
    document.getElementById("signup_popup_success_modal").close();
    onClose();
    navigate('/login');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-1 px-3">
          <img src={bidcaribbean} alt="BidCaribbean" className="h-8" />
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Headline */}
          <h2 className="text-xl font-bold text-center mb-4 text-gray-800">
            {selectedHeadline}
          </h2>

          {/* Benefits */}
          <div className="mb-6">
            <ul className="space-y-2">
              <li className="flex items-center text-gray-700">
                <span className="text-primary-red mr-2">✓</span>
                Bid live
              </li>
              <li className="flex items-center text-gray-700">
                <span className="text-primary-red mr-2">✓</span>
                Save favorites
              </li>
              <li className="flex items-center text-gray-700">
                <span className="text-primary-red mr-2">✓</span>
                Instant alerts
              </li>
            </ul>
          </div>

          {/* Step 1: Email */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your Email Address*"
                  className="w-full px-3 py-2 text-[14px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-red focus:border-transparent"
                />
              </div>
              
              <button
                onClick={handleEmailContinue}
                className="w-full bg-primary-red text-white font-medium text-[16px] rounded-lg py-2.5 transition-colors"
              >
                Continue
              </button>
            </div>
          )}

          {/* Step 2: Complete Registration */}
          {step === 2 && (
            <form onSubmit={formik.handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formik.values.username}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={
                    formik.touched.username && formik.errors.username
                      ? formik.errors.username
                      : "Your Full name*"
                  }
                  className={`w-full px-3 py-2 text-[14px] border rounded-lg ${
                   formik.touched.username
                      ? "border-red-600 placeholder-red-500"
                      : "border-gray-300"
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div>
                  <PhoneInput
                    country={"us"}
                    countryCodeEditable={true}
                    
                    disableDropdown={true}
                    buttonStyle={{
                      background: "white",
                      borderRight: "0px",
                      zIndex: 1
                    }}
                    containerClass="mx-auto border-none outline-none p-0 m-0"
                    inputProps={{
                      name: "phone",
                      required: true,
                      placeholder: formik.touched.phone && formik.errors.phone
                        ? formik.errors.phone
                        : "Your Phone Number*"
                    }}
                    inputStyle={{ 
                      width: "100%",
                      height: "40px",
                      fontSize: "14px",
                      borderRadius: "0.5rem",
                      border: formik.touched.phone && formik.errors.phone 
                        ? "1px solid #dc2626" 
                        : "1px solid #d1d5db",
                      paddingLeft: "48px",
                      zIndex: 0
                    }}
                    value={formik.values.phone}
                    onChange={(phone) => {
                      formik.setFieldValue("phone", phone);
                      formik.setFieldTouched("phone", true);
                    }}
                    onBlur={formik.handleBlur}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={
                    formik.touched.password && formik.errors.password
                      ? formik.errors.password
                      : "Your Password*"
                  }
                  className={`w-full px-3 py-2 text-[14px] border rounded-lg ${
                    formik.touched.password && formik.errors.password
                      ? "border-red-600 placeholder-red-500"
                      : "border-gray-300"
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={
                    formik.touched.confirmPassword && formik.errors.confirmPassword
                      ? formik.errors.confirmPassword
                      : "Confirm Password*"
                  }
                  className={`w-full px-3 py-2 text-[14px] border rounded-lg ${
                    formik.touched.confirmPassword && formik.errors.confirmPassword
                      ? "border-red-600 placeholder-red-500"
                      : "border-gray-300"
                  }`}
                />
              </div>

              {/* Show Password Checkbox */}
              <div className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={showPassword}
                  onChange={() => setShowPassword(!showPassword)}
                  className="form-checkbox text-sm"
                />
                <label>Show Password</label>
              </div>

              {/* Terms & Conditions Checkbox */}
              <div className="flex justify-start items-center gap-x-1.5 sm:gap-2">
                <input
                  type="checkbox"
                  id="terms"
                  className="rounded border-gray-300 mt-1 md:mt-0 w-[14px] h-[14px]"
                  onChange={(e) => setIsTermsChecked(e.target.checked)}
                />
                <label htmlFor="terms" className="text-[14px] text-left text-gray-600">
                  I Agree with the
                  <a href="/terms&conditions" target="_blank" className="text-black font-semibold hover:underline">
                    Terms & Conditions
                  </a>
                  <span className="text-gray-600 mx-1">and</span>
                  <a href="/privacy-policies" target="_blank" className="text-black font-semibold hover:underline">
                    Privacy Policy
                  </a>
                </label>
              </div>

              <button
                type="submit"
                disabled={!isTermsChecked || isLoading}
                className={`w-full bg-primary-red text-white font-medium text-[16px] rounded-lg py-2.5 transition-colors ${!isTermsChecked || isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isLoading ? (
                  <ClipLoader size={20} color="white" />
                ) : (
                  "Create Free Account"
                )}
              </button>
            </form>
          )}

          {/* Not Now Link */}
          <div className="text-center mt-4">
            <button
              onClick={handleNotNow}
              className="text-gray-500 hover:text-gray-700 text-sm underline"
            >
              Not now
            </button>
          </div>
        </div>
      </div>

      {/* Registration Success Modal */}
      <dialog id="signup_popup_success_modal" className="modal">
        <div className="modal-box dark:bg-white font-urbanist">
          <div className="text-center">
            <div className="mb-4 text-green-600">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h3 className="font-bold text-lg">Registration Successful!</h3>
            <p className="py-4">
              Thank you for registering with BidCaribbean. Please check your email to verify your account.
            </p>
            <div className="flex gap-x-2 justify-center">
              <button
                className="btn text-green-600 dark:bg.white hover:bg-gray-200 border-green-600"
                onClick={closeSuccessModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </dialog>
    </div>
  );
};

export default SignupPopup;
